import { c as _c } from "react/compiler-runtime";
import type { CertificadoAcessosArbitral } from '../../types/certificadoAcessosTypes';
import { type BlocoDocumento, DocumentoPaginado } from '@/shared/components/documentos/paginado';
import { blocosAuditoriaDestinatario } from './documento/blocosAuditoriaDestinatario';
import { blocosConversasWhatsapp } from './documento/blocosConversasWhatsapp';
import { blocosReplicaEmail } from './documento/blocosReplicaEmail';
import { MarcadorSecao, SECAO_DOCUMENTOS_CORREIOS, type SecaoCertificado, secoesCertificado, SumarioCertificado } from './documento/EstruturaDocumento';
import { SecaoAuditoriaIntro, TituloCanalAuditoria } from './secoes/SecaoAuditoria';
import { SecaoAutenticidade, SecaoSeguranca } from './secoes/SecaoAutenticidade';
import { SecaoCapa } from './secoes/SecaoCapa';
import { SecaoCartaCitacao } from './secoes/SecaoCartaCitacao';
import { DocumentoCorreiosBloco } from './secoes/SecaoDocumentosCorreios';
import { SecaoEncerramento } from './secoes/SecaoEncerramento';
import { PainelComprovacao, SecaoEventosIntro } from './secoes/SecaoEventosComprovados';
import { SecaoPartesIntro, TabelaPartesGrupo } from './secoes/SecaoPartes';
import { SecaoObjetivo, SecaoOQueEDocumento } from './secoes/SecaoSobreDocumento';
import { ComunicadosVazio, EmailDestinatarios, SecaoTextosIntro, TabelaAcessosDestinatario, TituloAcessosPlataforma } from './secoes/SecaoTextosEnviados';

/**
 * Sem carta postada, o bloco "Comprovações de envio — Correios (AR)" some, pela MESMA regra que
 * esconde a Seção 7 (`enviosCorreios` já chega filtrado pelo back a cartas efetivamente
 * postadas). Com uma regra só, as duas partes do documento nunca discordam sobre haver carta.
 */
function canaisComEnvio(certificado: CertificadoAcessosArbitral) {
  if (certificado.enviosCorreios.length > 0) return certificado.canaisAuditoria;
  return certificado.canaisAuditoria.filter(canal => canal.canal !== 'CORREIOS');
}
function construirBlocosAuditoria(certificado: CertificadoAcessosArbitral): BlocoDocumento[] {
  return canaisComEnvio(certificado).flatMap(canal => [{
    id: `auditoria-titulo-${canal.canal}`,
    manterComProximo: true,
    node: <TituloCanalAuditoria canal={canal.canal} />
  }, ...canal.destinatarios.flatMap((destinatario, index) => blocosAuditoriaDestinatario(canal, destinatario, `auditoria-${canal.canal}-${index}`))]);
}

// TODO(#835): estado vazio quando acessosPlataforma está vazio (hoje sai título sem tabela).
function construirBlocosAcessos(certificado: CertificadoAcessosArbitral): BlocoDocumento[] {
  return [{
    id: 'acessos-titulo',
    manterComProximo: true,
    node: <TituloAcessosPlataforma />
  }, ...certificado.acessosPlataforma.map((destinatario, index) => ({
    id: `acessos-${index}`,
    node: <TabelaAcessosDestinatario destinatario={destinatario} />
  }))];
}
function construirBlocosComunicados(certificado: CertificadoAcessosArbitral, secoes: readonly SecaoCertificado[]): BlocoDocumento[] {
  const marcador: BlocoDocumento = {
    id: 'marcador-6',
    manterComProximo: true,
    node: <MarcadorSecao numero={6} secoes={secoes} />
  };
  if (certificado.mensagensWhatsapp.length === 0 && certificado.mensagensEmail.length === 0 && certificado.respostasWhatsapp.length === 0) {
    return [marcador, {
      id: 'comunicados-vazio',
      node: <ComunicadosVazio />
    }];
  }
  return [marcador, {
    id: 'textos-intro',
    manterComProximo: true,
    node: <SecaoTextosIntro />
  }, ...blocosConversasWhatsapp(certificado.mensagensWhatsapp, 'conversa-whatsapp', certificado.respostasWhatsapp, certificado.instituicao.orgao), ...certificado.mensagensEmail.flatMap((mensagem, index) => [{
    id: `email-destinatarios-${index}`,
    manterComProximo: true,
    node: <EmailDestinatarios mensagem={mensagem} />
  }, ...blocosReplicaEmail(mensagem, `replica-email-${index}`)])];
}

/**
 * Seção 7 — Documentos dos Correios, inteira (divisória + cartas), ou NADA.
 *
 * Sem envio postal não existe seção: o back só manda envio efetivamente postado
 * (`cartaFoiPostada`, no `certificadoAcessosService`), então lista vazia aqui significa "não houve
 * carta AR neste processo". A divisória, que saía incondicionalmente com `novaPagina`, abria uma
 * página inteira anunciando um anexo inexistente (QA itens 6 e 10a).
 *
 * A posição no fim do documento é contrato de design e não muda — o que some é a seção toda,
 * junto com sua linha no sumário (ver `secoesCertificado`).
 */
function construirBlocosCorreios(certificado: CertificadoAcessosArbitral, secoes: readonly SecaoCertificado[]): BlocoDocumento[] {
  if (certificado.enviosCorreios.length === 0) return [];
  const marcador: BlocoDocumento = {
    id: 'marcador-7',
    novaPagina: true,
    manterComProximo: true,
    node: <MarcadorSecao numero={SECAO_DOCUMENTOS_CORREIOS} secoes={secoes} />
  };
  return [marcador, ...certificado.enviosCorreios.flatMap((envio, indexEnvio) => [{
    id: `carta-${indexEnvio}`,
    novaPagina: true,
    node: <SecaoCartaCitacao cartaCitacao={envio.carta} />
  }, {
    id: `comprovante-postagem-${indexEnvio}`,
    node: <DocumentoCorreiosBloco documento={envio.comprovantePostagem} destinatarioNome={envio.destinatarioNome} />
  }, {
    id: `ar-${indexEnvio}`,
    node: <DocumentoCorreiosBloco documento={envio.avisoRecebimento} destinatarioNome={envio.destinatarioNome} />
  }])];
}
function construirBlocos(certificado: CertificadoAcessosArbitral): BlocoDocumento[] {
  const secoes = secoesCertificado(certificado.enviosCorreios.length > 0);
  return [{
    id: 'capa',
    manterComProximo: true,
    node: <SecaoCapa certificado={certificado} />
  }, {
    id: 'sumario',
    manterComProximo: true,
    node: <SumarioCertificado secoes={secoes} />
  }, {
    id: 'capa-autenticidade',
    node: <SecaoAutenticidade codigoAutenticacao={certificado.codigoAutenticacao} urlAutenticacao={certificado.urlAutenticacao} urlAutenticacaoExibicao={certificado.urlAutenticacaoExibicao} />
  },
  // Seção 1 — Partes envolvidas
  {
    id: 'marcador-1',
    manterComProximo: true,
    node: <MarcadorSecao numero={1} secoes={secoes} />
  }, {
    id: 'partes-intro',
    manterComProximo: true,
    node: <SecaoPartesIntro />
  }, ...certificado.gruposPartes.map(grupo => ({
    id: `partes-${grupo.titulo}`,
    node: <TabelaPartesGrupo grupo={grupo} />
  })),
  // Seção 2 — Objetivo e escopo
  {
    id: 'marcador-2',
    manterComProximo: true,
    node: <MarcadorSecao numero={2} secoes={secoes} />
  }, {
    id: 'o-que-e-documento',
    node: <SecaoOQueEDocumento numeroProcesso={certificado.numeroProcesso} />
  }, {
    id: 'objetivo',
    node: <SecaoObjetivo paragrafos={certificado.objetivoParagrafos} />
  },
  // Seção 3 — Situação da comprovação por parte
  {
    id: 'marcador-3',
    manterComProximo: true,
    node: <MarcadorSecao numero={3} secoes={secoes} />
  }, {
    id: 'eventos-intro',
    manterComProximo: true,
    node: <SecaoEventosIntro />
  }, {
    id: 'resumo-comprovacao',
    node: <PainelComprovacao resumo={certificado.resumoComprovacao} />
  },
  // Seção 4 — Segurança do documento
  {
    id: 'marcador-4',
    manterComProximo: true,
    node: <MarcadorSecao numero={4} secoes={secoes} />
  }, {
    id: 'seguranca',
    node: <SecaoSeguranca paragrafos={certificado.textosSeguranca} hashCertificado={certificado.hashCertificado} versaoHash={certificado.versaoHash} cadeiaIntegra={certificado.cadeiaIntegra} cadeiaQuebradaNaVersao={certificado.cadeiaQuebradaNaVersao} />
  },
  // Seção 5 — Detalhes de auditoria
  {
    id: 'marcador-5',
    manterComProximo: true,
    node: <MarcadorSecao numero={5} secoes={secoes} />
  }, {
    id: 'auditoria-intro',
    manterComProximo: true,
    node: <SecaoAuditoriaIntro />
  }, ...construirBlocosAuditoria(certificado), ...construirBlocosAcessos(certificado),
  // Seção 6 — Comunicados enviados (divisória sempre presente; vazio explícito)
  ...construirBlocosComunicados(certificado, secoes),
  // Seção 7 — Documentos dos Correios (some inteira quando não houve carta postada)
  ...construirBlocosCorreios(certificado, secoes), {
    id: 'encerramento',
    node: <SecaoEncerramento certificado={certificado} />
  }];
}
interface CertificadoAcessosDocumentoProps {
  certificado: CertificadoAcessosArbitral;
}
export function CertificadoAcessosDocumento(t0) {
  const $ = _c(6);
  const {
    certificado
  } = t0;
  let t1;
  if ($[0] !== certificado) {
    t1 = construirBlocos(certificado);
    $[0] = certificado;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const blocos = t1;
  let t2;
  if ($[2] !== blocos || $[3] !== certificado.codigoAutenticacao || $[4] !== certificado.urlAutenticacaoExibicao) {
    t2 = <DocumentoPaginado blocos={blocos} codigoAutenticacao={certificado.codigoAutenticacao} urlAutenticacaoExibicao={certificado.urlAutenticacaoExibicao} />;
    $[2] = blocos;
    $[3] = certificado.codigoAutenticacao;
    $[4] = certificado.urlAutenticacaoExibicao;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}